import NeptuneElement from '../../../NeptuneElement.js';

class SidenavItem extends NeptuneElement {

    templateMarkup = `
        <li>
            <a>
                <span class="avatar" hidden></span>
                <i class="fa-regular" hidden></i>
                <span class="font-size-md"></span>
                <i class="fa-regular" hidden></i>
            </a>
           <neptune-tooltip placement="right">
                <a slot="trigger-element" hidden></a>
                <span slot="tooltip-text"></span>
            </neptune-tooltip>
        </li>
    `;

    componentCssUrl = '/src/components/chrome/sidenav/sidenavItem/sidenav-item.css';

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(SidenavItem.observedAttributes);
        requestAnimationFrame(() => this.#updateTruncationTooltip());
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['label', 'route', 'icon', 'image', 'active', 'expanded', 'submenu', 'collapsed', 'avatarText'];
    }

    setEventListeners() {
        this.linkElement = this.shadowRoot.querySelector('a');
        this.handleClickBound = this.#handleClick.bind(this);
        
        this.linkElement?.addEventListener('click', this.handleClickBound);

        this.triggerElement = this.shadowRoot.querySelector('neptune-tooltip a[slot="trigger-element"]');
        this.triggerElement?.addEventListener('click', this.handleClickBound);
    }

    removeEventListeners() {
        this.linkElement?.removeEventListener('click', this.handleClickBound);
        this.triggerElement?.removeEventListener('click', this.handleClickBound);
        this.linkElement = null;
        this.triggerElement = null;
        this.handleClickBound = null;
        this.linkElement?.removeEventListener('mouseenter', this.handleLabelHoverBound);
        this.handleLabelHoverBound = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const link = this.shadowRoot?.querySelector('a');
        const label = this.shadowRoot?.querySelector('span.font-size-md');
        const avatar = this.shadowRoot?.querySelector('span.avatar');
        const iconRight = this.shadowRoot?.querySelector('i:last-of-type');

        if (!link || !label) {
            throw new Error(`SidenavItem: Required elements not found in shadow DOM. Missing: ${!link ? 'link' : ''} ${!label ? 'label' : ''}`);
        }
        switch (name) {
            case 'label': {
                const labelText = newValue || '';
                label.textContent = labelText;
                const tooltip = this.shadowRoot?.querySelector('neptune-tooltip');
                if (tooltip) {
                    const tooltipText = tooltip.querySelector('span[slot="tooltip-text"]');
                    if (tooltipText) {
                        tooltipText.textContent = labelText;
                    }
                }
                requestAnimationFrame(() => this.#updateTruncationTooltip());
                break;
            }
            case 'route': {
                link.href = newValue || '#';
                break;
            }
            case 'icon':
            case 'image': {
                const iconLeft = link.querySelector('i:first-of-type');
                const existingImg = link.querySelector('img.sidenav-icon');
                if (existingImg) { existingImg.remove(); }

                if (newValue && this.#isImageUrl(newValue)) {
                    if (iconLeft) {
                        iconLeft.setAttribute('hidden', '');
                        iconLeft.className = 'fa-regular';
                    }
                    const img = document.createElement('img');
                    img.src = newValue;
                    img.className = 'sidenav-icon';
                    img.alt = 'icon';
                    link.insertBefore(img, link.querySelector('span.font-size-md'));
                } else if (newValue && iconLeft) {
                    iconLeft.classList.add(newValue);
                    iconLeft.removeAttribute('hidden');
                } else if (iconLeft) {
                    iconLeft.setAttribute('hidden', '');
                    iconLeft.className = 'fa-regular';
                }
                break;
            }
            case 'avatarText': {
                if (newValue && avatar) {
                    avatar.textContent = newValue;
                    avatar.removeAttribute('hidden');
                    const customIcon = link.querySelector('i:not(:last-of-type), img.sidenav-icon');
                    if (customIcon) {
                        customIcon.remove();
                    }
                } else if (avatar) {
                    avatar.setAttribute('hidden', '');
                }
                break;
            }
            case 'collapsed': {
                const isCollapsed = this.hasAttribute('collapsed');
                const labelText = this.getAttribute('label') || '';
                const tooltip = this.shadowRoot?.querySelector('neptune-tooltip');
                const tooltipTrigger = tooltip?.querySelector('a[slot="trigger-element"]');
                if (tooltip && tooltipTrigger) {
                    if (isCollapsed && labelText) {
                        tooltipTrigger.setAttribute('aria-label', labelText);
                        tooltipTrigger.removeAttribute('hidden');
                        const tooltipText = tooltip.querySelector('span[slot="tooltip-text"]');
                        if (tooltipText) { 
                            tooltipText.textContent = labelText; 
                        }
                    } else {
                        tooltipTrigger.setAttribute('hidden', '');
                    }
                }
                if (!isCollapsed) {
                    requestAnimationFrame(() => this.#updateTruncationTooltip());
                }
                break;
            }
            case 'submenu':
            case 'active':
            case 'expanded': {
                const isActive = this.hasAttribute('active');
                const isExpanded = this.hasAttribute('expanded');
                const hasSubmenu = this.hasAttribute('submenu');

                if (hasSubmenu) {
                    iconRight.className = `fa-solid ${isExpanded ? 'fa-chevron-left' : 'fa-chevron-right'}`;
                    iconRight.removeAttribute('hidden');
                } else {
                    iconRight.setAttribute('hidden', '');
                }
                let stateValue = 'default';
                if (isActive) {
                    stateValue = 'active';
                } else if (isExpanded) {
                    stateValue = 'expanded';
                }
                this.setAttribute('state', stateValue);
                break;
            }
            default: {
                break;
            }
        }
    }

    #handleClick(event) {
        const hasSubmenu = this.hasAttribute('submenu');

        if (hasSubmenu) {
            event.preventDefault();
            const isExpanded = this.hasAttribute('expanded');
            this.toggleAttribute('expanded', !isExpanded);

            this.dispatchEvent(new CustomEvent('neptune-sidenav-item-toggle', {
                bubbles: true,
                composed: true,
                detail: {
                    expanded: !isExpanded,
                    label: this.getAttribute('label')
                }
            }));
        } else {
            event.preventDefault();
            this.dispatchEvent(new CustomEvent('neptune-sidenav-item-click', {
                bubbles: true,
                composed: true,
                detail: {
                    id: this.getAttribute('data-item-id'),
                    route: this.getAttribute('route'),
                    label: this.getAttribute('label')
                }
            }));
        }
    }

    /**
    * Shows a tooltip when the label text is truncated.
    */
    #updateTruncationTooltip() {
        if (this.hasAttribute('collapsed')) {
            this.removeAttribute('truncated'); 
            return; 
        }
        
        const label = this.shadowRoot?.querySelector('span.font-size-md');
        const tooltip = this.shadowRoot?.querySelector('neptune-tooltip');
        const trigger = tooltip?.querySelector('a[slot="trigger-element"]');
        
        if (!label || !tooltip || !trigger) { return; }

        const isTruncated = label.scrollWidth > label.clientWidth + 2;
        if (isTruncated) {
            const labelText = this.getAttribute('label') || '';
            const tooltipText = tooltip.querySelector('span[slot="tooltip-text"]');
            if (tooltipText) { tooltipText.textContent = labelText; }
            trigger.setAttribute('aria-label', labelText);
            trigger.removeAttribute('hidden');
            this.setAttribute('truncated', '');
        } else {
            trigger.setAttribute('hidden', '');
            this.removeAttribute('truncated');
        }
    }

    refreshTooltip() {
        this.#updateTruncationTooltip();
    }

    /**
     * Determines if a string is an image URL or Font Awesome class.
     * @param {string} icon - The icon value to check
     * @returns {boolean} True if it's a URL, false if it's a Font Awesome class
     */
    #isImageUrl(icon) {
        return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(icon) || 
               icon.startsWith('http://') || 
               icon.startsWith('https://') ||
               icon.startsWith('/') ||
               icon.startsWith('data:');
    }

}

if (!customElements.get('neptune-sidenav-item')) {
    customElements.define('neptune-sidenav-item', SidenavItem);
}

export default SidenavItem;